import { fork } from '@cortex/utils/fork';

import { clearAuthMode } from './auth-mode';
import type { IAuthStrategy } from './auth-strategy';
import type { IFormantAuthClient } from './formant-auth-client';

/**
 * Browser surface the Coherence strategy needs, injected so the strategy is
 * unit-testable without a real DOM / navigation.
 */
export interface ICoherenceEnv {
  getSearch(): string;
  getHostname(): string;
  getHref(): string;
  getCookie(): string;
  getStoredRefreshToken(): string | null;
  setStoredRefreshToken(token: string | null): void;
  /** Remove the OTHER provider's residual localStorage (Keycloak `oidc.*`
   *  entries) on sign-out — "no leftovers" across a provider switch. */
  clearForeignAuthStorage(): void;
  /** The `?token=` handoff nonce (sessionStorage) — set before redirecting to
   *  Coherence, checked against the echoed `cstate` on return. */
  getStoredNonce(): string | null;
  setStoredNonce(nonce: string | null): void;
  replaceSearch(cleanedSearch: string): void;
  redirect(url: string): void;
}

/** localStorage key for the console's own copy of the Formant refresh token. */
const refreshTokenStorageKey = 'cortex.formantRefreshToken';
/** sessionStorage key for the one-time `?token=` handoff nonce. */
const nonceStorageKey = 'cortex.coherenceLoginNonce';

/** Random, unguessable nonce for the token-handoff CSRF binding. Prefers
 *  `randomUUID`, then `getRandomValues` (which — unlike `randomUUID` — is
 *  available in insecure/`http:` contexts too); the last fallback is
 *  non-cryptographic and only reached where neither Web Crypto API exists. */
function generateNonce(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function cookieEnvPrefix(hostname: string): string {
  if (hostname === 'localhost') return '-dev';
  if (hostname.includes('-dev.formant.io')) return '-dev';
  if (hostname.includes('-stage.formant.io')) return '-stage';
  if (hostname.includes('.formant.io')) return '-prod';
  return '-dev';
}

/**
 * Read the shared Coherence session token from the `.formant.io` cookie
 * (`currentToken<env>`), matching @formant/auth-lib's key convention so we
 * interoperate with the token Coherence writes.
 */
export function readCoherenceCookieToken(hostname: string, cookie: string): string | null {
  const key = `currentToken${cookieEnvPrefix(hostname)}`;
  for (const part of cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() === key) {
      return decodeURIComponent(part.slice(eq + 1).trim());
    }
  }
  return null;
}

/** Hosts that can't read Coherence's shared `.formant.io` cookie, so they need
 *  the one-time `?token=` handoff: localhost, Vercel previews, and — crucially —
 *  the deployed metaphysics console's own `*.formant.ai` domain. Without
 *  `.formant.ai` here the deployed console had no credential path and looped on
 *  sign-in. We ONLY read `?token=` on these hosts (below), which also limits the
 *  handoff's attack surface on cookie-capable prod hosts. */
function usesTokenHandoff(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('vercel.app') ||
    hostname.includes('.formant.ai')
  );
}

/** Hosts where the stored refresh token can go stale relative to Coherence's
 *  live session, so `init` must re-handshake instead of booting from it. These
 *  are exactly the hosts that can't read the `.formant.io` session cookie — so
 *  the only current-user signal is a fresh `?token=` handoff: localhost, Vercel,
 *  and the deployed `*.formant.ai` consoles. (After a Coherence user-switch the
 *  stored token is a stale *previous* user — still valid, just wrong.)
 *  Cookie-capable `.formant.io` hosts read the live session directly, so they
 *  keep the stored-token fallback. Same host set as `usesTokenHandoff`. */
function mustReconcileWithCoherence(hostname: string): boolean {
  return usesTokenHandoff(hostname);
}

/** Build the Coherence login redirect (returns here; requests a token handoff
 *  on hosts that can't share the `.formant.io` cookie). Mirrors
 *  @formant/auth-lib's `goToFormantLogin`.
 *
 *  `signup=1` is the only thing that makes Coherence's login page show its
 *  "Create an account" link; Metaphysics is the sole product that sets it. */
function coherenceLoginRedirect(loginUrl: string, env: ICoherenceEnv): string {
  const url = new URL(loginUrl);
  url.searchParams.set('signup', '1');
  const returnUrl = new URL(env.getHref());
  // Never echo a stale credential back to Coherence: if the current URL still
  // carries a `token`/`cstate` (e.g. a retry after a failed refresh), strip it
  // so we don't send `redirectUrl=…&token=<refresh>` into Coherence's logs.
  returnUrl.searchParams.delete('token');
  returnUrl.searchParams.delete('cstate');
  if (usesTokenHandoff(env.getHostname())) {
    // Bind the ?token= handoff to a login WE initiated: stash a nonce and echo
    // it via the redirectUrl's `cstate`. Coherence preserves the redirectUrl's
    // query verbatim and only appends `?token=`, so `cstate` returns with the
    // token; `init` rejects a token whose cstate doesn't match (login-CSRF).
    const nonce = generateNonce();
    env.setStoredNonce(nonce);
    returnUrl.searchParams.set('cstate', nonce);
    url.searchParams.set('includeToken', 'true');
  }
  url.searchParams.set('redirectUrl', returnUrl.toString());
  return url.toString();
}

/** Build the Coherence *logout* redirect: the login redirect (carrying
 *  `redirectUrl` = the current page, + `includeToken` on cookie-incapable hosts)
 *  plus `logout=true`.
 *
 *  `logout=true` makes Coherence clear the shared `.formant.io` session and show
 *  its sign-in form. Its logout branch runs BEFORE the auto-redirect branch and
 *  returns early, so a live session can't bounce us straight back in (the
 *  "logout is a no-op" bug). The `redirectUrl` we pass is ignored on that logout
 *  render but persists in the address bar, so once the user re-authenticates
 *  Coherence sends them back to the same page they logged out from. */
function coherenceLogoutRedirect(loginUrl: string, env: ICoherenceEnv): string {
  const url = new URL(coherenceLoginRedirect(loginUrl, env));
  url.searchParams.set('logout', 'true');
  return url.toString();
}

/** Default `ICoherenceEnv` backed by the real browser. */
export function browserCoherenceEnv(): ICoherenceEnv {
  return {
    getSearch: () => window.location.search,
    getHostname: () => window.location.hostname,
    getHref: () => window.location.href,
    getCookie: () => document.cookie,
    getStoredRefreshToken: () => localStorage.getItem(refreshTokenStorageKey),
    setStoredRefreshToken: (t) => {
      if (t) localStorage.setItem(refreshTokenStorageKey, t);
      else localStorage.removeItem(refreshTokenStorageKey);
    },
    clearForeignAuthStorage: () => {
      // Nuke any Keycloak oidc-client-ts residue so a Coherence sign-out doesn't
      // leave a stale KC session behind (and vice-versa in keycloak-auth).
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('oidc.')) localStorage.removeItem(k);
      }
    },
    getStoredNonce: () => sessionStorage.getItem(nonceStorageKey),
    setStoredNonce: (n) => {
      if (n) sessionStorage.setItem(nonceStorageKey, n);
      else sessionStorage.removeItem(nonceStorageKey);
    },
    replaceSearch: (s) =>
      window.history.replaceState({}, '', `${window.location.pathname}${s}${window.location.hash}`),
    redirect: (url) => window.location.assign(url),
  };
}

export interface ICoherenceAuthDeps {
  client: IFormantAuthClient;
  coherenceLoginUrl: string;
  env: ICoherenceEnv;
}

/**
 * Coherence sign-in strategy (default path).
 *
 * The session credential is a Formant *refresh token*, obtained from — in
 * priority order — the one-time `?token=` handoff (localhost / cookie-incapable
 * hosts), the shared `.formant.io` cookie (deployed suite hosts on `.formant.io`),
 * or the console's own stored copy. On cookie-incapable hosts (localhost, Vercel,
 * `*.formant.ai`) the stored copy is deliberately NOT used for initial login — it
 * can go stale across a Coherence user-switch (see `mustReconcileWithCoherence`),
 * so `init` emits null and the shell re-handshakes with Coherence for the current
 * user. `init`
 * exchanges the credential for an access token (+ display fields) and emits the
 * session; it never redirects. `signIn`/`signOut` redirect to Coherence.
 *
 * Token refresh on expiry is handled reactively: a 401 drives the shell's
 * `onUnauthorized` → `signIn`, which re-issues silently via Coherence's own
 * session. (Proactive pre-expiry refresh is a deliberate follow-up.)
 */
export function createCoherenceAuth(deps: ICoherenceAuthDeps): IAuthStrategy {
  const { client, coherenceLoginUrl, env } = deps;

  return {
    async init(onSession) {
      const search = new URLSearchParams(env.getSearch());
      // Only trust the `?token=` handoff on hosts we actually request it for
      // (ignoring a crafted `?token=` on cookie-capable hosts), AND only when it
      // carries the `cstate` nonce we stashed before redirecting — so a handoff
      // the user didn't initiate is rejected (login-CSRF).
      let queryToken = usesTokenHandoff(env.getHostname()) ? search.get('token') : null;
      if (queryToken) {
        const expected = env.getStoredNonce();
        env.setStoredNonce(null); // one-time use
        if (!expected || search.get('cstate') !== expected) {
          queryToken = null; // unbound / forged handoff — do not trust it
        }
      }

      // Strip `token`/`cstate` from the address bar BEFORE any await — so a
      // failed refresh (or an untrusted handoff) can't leave the refresh token
      // in the URL or browser history. Runs whenever they're present, not only
      // on the success path.
      if (search.has('token') || search.has('cstate')) {
        search.delete('token');
        search.delete('cstate');
        const qs = search.toString();
        env.replaceSearch(qs ? `?${qs}` : '');
      }

      // On cookie-incapable hosts (localhost, Vercel, `*.formant.ai`) the stored
      // refresh token can be a stale *previous* user (see
      // `mustReconcileWithCoherence`), so we skip it there and emit null —
      // main.tsx then re-handshakes with Coherence (`?includeToken`) for the
      // current user. The stored token still serves `getConnectTokens` and
      // cookie-capable hosts. queryToken (a fresh handoff) and the readable
      // cookie always win, so a just-returned handoff is used immediately.
      const refreshToken =
        queryToken ??
        readCoherenceCookieToken(env.getHostname(), env.getCookie()) ??
        (mustReconcileWithCoherence(env.getHostname()) ? null : env.getStoredRefreshToken());

      if (!refreshToken) {
        onSession(null);
        return;
      }

      try {
        const session = await client.refresh(refreshToken);
        // Persist the rotated refresh token for this origin.
        env.setStoredRefreshToken(session.refreshToken);
        // Emit the authenticated session as soon as we hold the access token —
        // that alone is what the shell needs to render, so `ready` flips after
        // one round-trip instead of three. email/name/orgName are best-effort
        // chrome (the nav falls back to a token-derived org alias meanwhile);
        // fetch them in the background and re-emit, rather than parking the
        // whole app on "Loading session…" for two extra round-trips.
        onSession({ token: session.accessToken, email: null, name: null, orgName: null });
        fork(
          (async () => {
            const [info, orgName] = await Promise.all([
              client.getUserInfo(session.userId, session.accessToken),
              client.getOrgName(session.organizationId, session.accessToken),
            ]);
            onSession({ token: session.accessToken, email: info.email, name: info.name, orgName });
          })(),
        );
      } catch {
        onSession(null);
      }
    },

    async getConnectTokens() {
      // Exchange the stored refresh token for a fresh, matched access+refresh
      // pair (and persist the rotation) — the same round-trip `init` does.
      const rt = env.getStoredRefreshToken();
      if (!rt) return null;
      try {
        const session = await client.refresh(rt);
        env.setStoredRefreshToken(session.refreshToken);
        return { accessToken: session.accessToken, refreshToken: session.refreshToken };
      } catch {
        return null;
      }
    },

    async signIn() {
      env.redirect(coherenceLoginRedirect(coherenceLoginUrl, env));
    },

    async signOut() {
      env.setStoredRefreshToken(null);
      env.clearForeignAuthStorage();
      clearAuthMode();
      // Global SSO logout: clear the console's copy AND ask Coherence to drop
      // the shared session (`?logout=true`). We keep `redirectUrl` = the current
      // page so re-authenticating returns here — the logout contract clears the
      // session first, so this can't auto-bounce us back in.
      env.redirect(coherenceLogoutRedirect(coherenceLoginUrl, env));
    },
  };
}
